/*
* This file is for functions that can be used in multiples places in the site. (Ex: API call for inventory check)
* */
if (!customElements.get('bosapin-utils')) {
  customElements.define('bosapin-utils', class BosapinUtils extends HTMLElement {
    constructor() {
      super();

      this.apiBaseUrl = Shopify.treasy_api_url;
      this.minimumCartPurchase = 2000;

      // Add code that needs to be executed on page load here
    }

    enableLoading() {
      const main = document.querySelector('#MainContent');
      main.style.opacity = '0.5';
      main.style.pointerEvents = 'none';
    }

    disableLoading() {
      const main = document.querySelector('#MainContent');
      main.style.opacity = '1';
      main.style.pointerEvents = 'all';
    }

    getLocale() {
      return document.documentElement.lang;
    }

    async apiCheckInventoryProduct(productId, postalCode) {
      const postalCodeStorage = this.normalizePostalCode(localStorage.getItem('postalCode'));
      if (!postalCode) {
        postalCode = postalCodeStorage;
      }

      if (!productId || !postalCode) {
        return false;
      }

      try {
        const response = await fetch(`${this.apiBaseUrl}api/validate-inventory`, {
          ...this.fetchConfig(),
          body: JSON.stringify({
            product: productId,
            postal_code: postalCode,
            shopify_url: Shopify.shop
          }),
          signal: AbortSignal.timeout(10000)
        });

        return await response.json();
      } catch (error) {
        if (error.name === 'TimeoutError' || error.name === 'AbortError') {
          return {'success': false, 'message': 'api_error', error: 'request_timeout'};
        }

        console.log(error);
      }
    }

    async apiCheckInventoryVariants(variants) {
      const postalCode = localStorage.getItem('postalCode');
      if (!postalCode || postalCode === '' || !variants || variants === '') {
        return false;
      }

      try {
        const response = await fetch(`${this.apiBaseUrl}api/validate-inventory`, {
          ...this.fetchConfig(),
          body: JSON.stringify({
            variants: variants,
            postal_code: postalCode,
            shopify_url: Shopify.shop
          }),
          signal: AbortSignal.timeout(10000)
        });

        return await response.json();
      } catch (error) {
        if (error.name === 'TimeoutError' || error.name === 'AbortError') {
          return {'success': false, 'message': 'api_error', error: 'request_timeout', data: []};
        }

        console.log(error);
      }
    }

    async apiValidatePostalCode(postalCode, bypassHighTrafficMode) {
      if (!postalCode) {
        return false;
      }

      this.highTrafficMode = false;
      

      if (this.highTrafficMode === true && !bypassHighTrafficMode) {
        const url = '//ottawa-gatineau.treasytrees.ca/cdn/shop/t/3/assets/postal_codes.csv?v=102085519002864755351779215630';
        window.dataLayer = window.dataLayer || [];

        // Fetch and parse CSV file
        const response = await fetch(url);
        const text = await response.text();
        const lines = text.split('\n');
        
        // Skip header line and parse data
        const postalCodeData = lines.slice(1).map(line => {
          const columns = line.split(',');
          return {
            code: this.normalizePostalCode(columns[0]),
            city: columns[1]?.trim(),
            province: columns[2]?.trim(),
            region: columns[3]?.trim(),
            companyId: columns[5]?.trim(),
            warehouseId: columns[6]?.trim(),
            dates_std: columns[7]?.trim().replace('"', '').split(',').filter(d => d && !d.includes('x')),
            dates_flex: columns[8]?.trim().replace('"', '').split(',').filter(d => d && !d.includes('x'))
          };
        }).filter(item => item.code); // Remove empty lines

        // Normalize postal code for comparison
        const normalizedPostalCode = this.normalizePostalCode(postalCode);
        
        // Try to find exact match or first 3 characters match
        let matchedData = postalCodeData.find(item => item.code === normalizedPostalCode);
        let matchedPostalCode = normalizedPostalCode;
        
        if (!matchedData) {
          const postalCodePrefix = normalizedPostalCode.substring(0, 3);
          matchedData = postalCodeData.find(item => item.code === postalCodePrefix);
          matchedPostalCode = postalCodePrefix;
        }

        // Postal code not found in CSV
        if (!matchedData) {
          if (this.sendUnknownPostalCode) {
            await fetch(`${this.apiBaseUrl}api/add-searched-postal-code/${normalizedPostalCode}`);
          }
          return {
            postalcode: normalizedPostalCode,
            region: "",
            success: false,
            message: "postal_code_not_found",
            dates_std: [],
            dates_flex: []
          };
        }

        // Check if postal code belongs to another company
        if (matchedData.companyId !== this.currentCompany) {
          const otherCompany = this.companies[matchedData.companyId];
          return {
            success: false,
            message: "postal_code_found_in_another_franchise",
            postal_code: normalizedPostalCode,
            postal_code_app: matchedPostalCode,
            company: otherCompany?.name || 'Unknown Company',
            website: otherCompany?.url || ''
          };
        }

        // Postal code found and belongs to current company
        // Format dates to match API structure
        // If a date as a x in the string, it means that the date is not available, remove it.
        const formattedStdDates = matchedData.dates_std.filter(date => !date.includes('x')).map((date) => ({
          date: date.trim(),
          start_time: "",
          end_time: "",
          type: "standard"
        }));

        const formattedFlexDates = matchedData.dates_flex.filter(date => !date.includes('x')).map((date) => ({
          date: date.trim(),
          start_time: "",
          end_time: "",
          type: "flex"
        }));

        // If no dates are available, return no_dates_available
        if (formattedStdDates.length === 0 && formattedFlexDates.length === 0) {
          return {
            postalcode: matchedPostalCode,
            region: matchedData.region,
            success: false,
            message: "no_dates_available",
            dates_std: [],
            dates_flex: []
          };
        }

        return {
          postalcode: matchedPostalCode,
          region: matchedData.region,
          success: true,
          message: "available",
          dates_std: formattedStdDates,
          dates_flex: formattedFlexDates,
          warehouse: {
            id: matchedData.warehouseId || null,
            name: matchedData.region // Using region as warehouse name for now
          }
        };

      } else {
        try {
          const response = await fetch(`${this.apiBaseUrl}api/validate-postalcode`, {
            ...this.fetchConfig(),
            body: JSON.stringify({
              postal_code: postalCode,
              shopify_url: Shopify.shop
            }),
            signal: AbortSignal.timeout(10000)
          });

          return await response.json();
        } catch (error) {
          if (error.name === 'TimeoutError' || error.name === 'AbortError') {
            return {'success': false, 'message': 'api_error', error: 'request_timeout'};
          }

          return {'success': false, 'message': 'api_error', error: error};
        }
      }
    }

    async apiGetItemAvailability(productId, region) {
      if (!region || region === '') {
        return false;
      }
      const postalCode = localStorage.getItem('postalCode');
      if (!postalCode || postalCode === '') {
        return false;
      }
      if (!productId || productId === '') {
        return false;
      }
      const response = await fetch(`${this.apiBaseUrl}api/validate-inventory/${postalCode}/product/${encodeURIComponent(productId)}`);
      return await response.json();
    }

    async isVariantInStock(productId, variantId, quantity, postalCode) {
      const stockResponse = await this.apiCheckInventoryProduct(productId, postalCode);
      if (!stockResponse) {
        return {
          inStock: false,
          available: 0
        };
      }

      if (!stockResponse.success || stockResponse.data.length <= 0) {
        return false;
      }

      const stockInformation = stockResponse.data;
      const currentVariant = stockInformation.variants.find((variant) => {
        return parseInt(variant.shopify_variant_id) === parseInt(variantId);
      });

      if (!currentVariant) {
        return false;
      }

      const availableQuantity = parseInt(currentVariant.quantity_available);
      if (availableQuantity >= quantity) {
        return {
          inStock: true,
          available: availableQuantity,
          stockInformation
        };
      }

      return {
        inStock: false,
        available: availableQuantity,
        stockInformation
      };
    }

    applyFormatMask(input, format) {
      const patterns = {
        'L': '[A-Za-z]',
        'N': '\\d'
      };

      format = 'LNLNLN';
      input = input.replace(' ', '');

      let regexPattern = '^';
      for (let char of format) {
        regexPattern += patterns[char] || char;
      }
      regexPattern += '$';

      return new RegExp(regexPattern).test(input.trim());
    }


    getRegexResult(postalCode) {
      return this.applyFormatMask(postalCode, 'LNLNLN');
    }

    normalizePostalCode(postalCode) {
      if (!postalCode) {
        return '';
      }

      return postalCode.replace(/\s/g, '').toUpperCase();
    }

    async fetchTreasyAvailability(postalCode, productId) {
      const normalized = this.normalizePostalCode(postalCode);
      if (!normalized || !productId) {
        return { success: false, message: 'error' };
      }

      try {
        const response = await fetch(`${this.apiBaseUrl}api/treasy/availability`, {
          ...this.fetchConfig(),
          body: JSON.stringify({
            postal_code: normalized,
            shopify_url: Shopify.shop,
            product_id: String(productId),
            language_code: document.documentElement.lang.substring(0, 2)
          }),
          signal: AbortSignal.timeout(10000)
        });

        return await response.json();
      } catch (error) {
        if (error.name === 'TimeoutError' || error.name === 'AbortError') {
          return { success: false, message: 'api_error', error: 'request_timeout' };
        }

        return { success: false, message: 'api_error', error: error };
      }
    }

    syncPostalAvailabilitySession(formattedPostalCode, result) {
      localStorage.setItem('region', '');
      localStorage.setItem('validPC', '');
      localStorage.setItem('postalCode', '');
      if (result.success) {
        localStorage.setItem('region', result.region || '');
        localStorage.setItem('validPC', 'true');
        localStorage.setItem('postalCode', formattedPostalCode);

        this.dataLayerPush({
          'event': 'postal_code_validation',
          'postal_code_validation': {
            'postal_code': formattedPostalCode,
            'status': 'available',
            'tool_location': window.postalCodeLocation
          }
        });
        return;
      }

      if (result?.message === 'postal_code_found_in_another_franchise') {
        this.dataLayerPush({
          'event': 'postal_code_validation',
          'postal_code_validation': {
            'postal_code': formattedPostalCode,
            'status': 'postal_code_found_in_another_franchise',
            'tool_location': window.postalCodeLocation
          }
        });
      } else {
        this.dataLayerPush({
          'event': 'postal_code_validation',
          'postal_code_validation': {
            'postal_code': formattedPostalCode,
            'status': 'not_found',
            'tool_location': window.postalCodeLocation
          }
        });
      }
    }

    async validatePostalCode(postalCode, bypassHighTrafficMode) {
      const formattedPostalCode = this.normalizePostalCode(postalCode);
      const isPostalCodeValid = this.getRegexResult(formattedPostalCode);
      if (isPostalCodeValid) {
        try {
          const result = await this.apiValidatePostalCode(formattedPostalCode, bypassHighTrafficMode);
          localStorage.setItem('region', '');
          localStorage.setItem('validPC', '');
          localStorage.setItem('postalCode', '');
          if (result.success) {
            localStorage.setItem('region', result.region || '');
            localStorage.setItem('validPC', 'true');
            localStorage.setItem('postalCode', formattedPostalCode);

            this.dataLayerPush({
              'event': 'postal_code_validation',
              'postal_code_validation': {
                'postal_code': formattedPostalCode,
                'status': 'available',
                'tool_location': window.postalCodeLocation
              }
            });

            return { success: true, message: result.message, data: result };
          }

          if (result?.message === 'postal_code_found_in_another_franchise') {
            this.dataLayerPush({
              'event': 'postal_code_validation',
              'postal_code_validation': {
                'postal_code': formattedPostalCode,
                'status': 'postal_code_found_in_another_franchise',
                'tool_location': window.postalCodeLocation
              }
            });
          } else {
            this.dataLayerPush({
              'event': 'postal_code_validation',
              'postal_code_validation': {
                'postal_code': formattedPostalCode,
                'status': 'not_found',
                'tool_location': window.postalCodeLocation
              }
            });
          }

          return { success: false, message: result.message, data: result };
        } catch (error) {
          console.log(error);
          return { success: false, message: 'error' };
        }
      } else {
        return { success: false, message: 'format' };
      }
    }

    async getCurrentItemVariantsAvailability(productId, region) {
      const self = this;

      if (!region) {
        region = localStorage.getItem('region');
      }
      const postalCode = localStorage.getItem('postalCode');
      if (!postalCode || postalCode === '') {
        return false;
      }

      if (region) {
        try {
          const result = await this.apiGetItemAvailability(productId, region);

          if (result.success === true) {
            return {success: true, data: result};
          }
          return {success: false, message: 'no_success'};
        } catch (error) {
          console.log(error);
          return {success: false, message: 'api_error'};
        }
      } else {
        return {success: false, message: 'no_region'};
      }
    }

    updateCartStorageData() {
      this.enableLoading();
      fetch(window.Shopify.routes.root + 'cart', {
        ...fetchConfig(),
        ...{}
      })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        //Region from here
        this.disableLoading();
        const parsedState = JSON.parse(state);
        localStorage.setItem('cart', JSON.stringify(parsedState.items));
        localStorage.setItem('totalPrice', JSON.stringify(parsedState.original_total_price));
      });
    }

    async updateQuantity(line, quantity) {
      const body = JSON.stringify({
        line,
        quantity
      });

      try {
        const response = await fetch(`${routes.cart_change_url}`, {...fetchConfig(), ...{body}});
        return await response.json();
      } catch (error) {
        console.log(error);
      }
    }

    async addToCart(id, quantity, attributes = {}) {
      const body = JSON.stringify({
        id,
        quantity,
        attributes
      });

      try {
        const response = await fetch(`${routes.cart_add_url}`, {...this.fetchConfig(), ...{body}});
        return await response.json();
      } catch (error) {
        console.log(error);
      }
    }

    async updateCart(updates) {
      const body = JSON.stringify(updates);

      try {
        const response = await fetch(`${routes.cart_update_url}`, {...this.fetchConfig(), ...{body}});
        return await response.json();
      } catch (error) {
        console.log(error);
      }
    }

    async clearCart() {
      try {
        // Clear cart items
        await fetch(`${routes.cart_clear}`);

        // Fetch cart attributes
        let cartData = await this.getCartDataAsync();

        // Create an object from those attributes we fetched and set them to null
        var nullHash = {};
        for (const [key, value] of Object.entries(cartData.attributes)) {
          nullHash[key] = null;
        }

        // Update the cart attributes to null
        const body = JSON.stringify({
          attributes: nullHash
        });
        await fetch(`${routes.cart_update_url}`, {...fetchConfig(), ...{body}});
      } catch (error) {
        console.log(error);
      }
    }

    getCartData() {
      return fetch(window.Shopify.routes.root + 'cart', {
        ...fetchConfig(),
        ...{}
      });
    }

    async getCartDataAsync() {
      const response = await fetch(window.Shopify.routes.root + 'cart', {
        ...fetchConfig(),
        ...{}
      });

      return response.json();
    }

    async updateCartStorageDataAsync() {
      const response = await fetch(window.Shopify.routes.root + 'cart', {
        ...fetchConfig(),
        ...{}
      });
      const cartData = await response.json();
      localStorage.setItem('cart', JSON.stringify(cartData.items));
      localStorage.setItem('totalPrice', JSON.stringify(cartData.original_total_price));
    }

    formatMoney(cents, format, precision, trailing) {
      if (typeof cents == 'string') {
        cents = cents.replace('.', '');
      }
      var value = '';
      var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      var formatString = (format || Shopify.money_format);

      function defaultOption(opt, def) {
        return ((typeof opt == 'undefined' || opt === false) ? def : opt);
      }

      function formatWithDelimiters(number, precision, trailing, thousands, decimal) {
        precision = defaultOption(precision, 2);
        thousands = defaultOption(thousands, ',');
        decimal = defaultOption(decimal, '.');
        trailing = defaultOption(trailing, false);

        if (isNaN(number) || number == null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        var parts = number.split('.'),
                dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
                cents = parts[1] ? (decimal + parts[1]) : '';

        var price = dollars + cents;

        if (trailing) {
          price.replace(/[\.,]0{2}/, '');
        }

        return price;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, precision, trailing);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, precision, trailing);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, precision, trailing, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, precision, trailing, '.', ',');
          break;
      }
      return formatString.replace(placeholderRegex, value);
    }

    getLongDate(date) {
      const weekDay = new Intl.DateTimeFormat(document.documentElement.lang, {weekday: 'long'}).format(new Date(`${date.trim()}T11:00:00`));
      const capitalizedWeekDay = weekDay.charAt(0).toUpperCase() + weekDay.slice(1);

      return `${capitalizedWeekDay} - ${date}`;
    }

    dataLayerPush(object) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(object);
    }

    fetchConfig(type = 'json') {
      return {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/${type}`
        }
      };
    }

    async getOnePagerData($postalCode) {
      const normalizedPostalCode = this.normalizePostalCode($postalCode);
      const response = await fetch(`${this.apiBaseUrl}api/treasy/one-pager`, {
        ...this.fetchConfig(),
        body: JSON.stringify({
          shopify_url: Shopify.shop,
          postal_code: normalizedPostalCode
        })
      });
      if (!response.ok) {
        throw new Error('one_pager_fetch_failed');
      }
      return await response.json();
    }
  });
  if (!customElements.get('add-to-cart')) {
    customElements.define('add-to-cart', class AddToCart extends HTMLElement {
      constructor() {
        super();

        this.bosapinUtils = document.querySelector('bosapin-utils');
        this.addEventListener('click', async (e) => {
          e.preventDefault();
          const productId = this.dataset.productId;
          const variantId = this.dataset.variantId;
          let quantity = parseInt(this.dataset.quantity) || 1;

          if (variantId) {
            try {
              this.bosapinUtils.enableLoading();

              const cartData = await this.bosapinUtils.getCartDataAsync();
              const itemFound = cartData.items.find(function (element) {
                return parseInt(element.variant_id) === parseInt(variantId)
              });
              let inventoryQuantity = quantity;
              if (itemFound) {
                inventoryQuantity = parseInt(itemFound.quantity) + parseInt(quantity);
              }

              // Validate inventory
              const inventoryData = await this.bosapinUtils.isVariantInStock(productId, variantId, inventoryQuantity);
              if (!inventoryData.inStock) {
                const $cartPopup = document.querySelector('cart-popup');
                $cartPopup.setContent($cartPopup.translations.no_stock_product);
                $cartPopup.setButtonText($cartPopup.translations.close);
                $cartPopup.openPopup();
                this.bosapinUtils.disableLoading();
                return false;
              }

              const addResponse = await this.bosapinUtils.addToCart(variantId, quantity);
              if (addResponse.id) {
                this.bosapinUtils.updateCartStorageData();

                const cartItems = document.querySelector('cart-items');
                if (cartItems) {
                  cartItems.currentItemCount = addResponse.item_count;
                  if (typeof cartItems.updateCartSections === 'function') {
                    await cartItems.updateCartSections();
                  }
                  this.scrollAddedLineIntoView(cartItems, variantId);
                }
              }
            } catch (error) {
              this.bosapinUtils.disableLoading();
            }
            this.bosapinUtils.disableLoading();
          } else {
            console.log('No variant ID found');
          }
        });
      }

      scrollAddedLineIntoView(cartItems, variantId) {
        const addedQty = Array.from(cartItems.querySelectorAll('.cart-item__quantity')).find((el) => {
          return String(el.dataset.variantId) === String(variantId);
        });
        const line = addedQty && addedQty.closest('.cart-item');
        const target = line || cartItems;
        // Fixed clearance: the sticky header is often translated away at the bottom of the cart,
        // then slides back over the line once we scroll up.
        const scrollMargin = 200;
        const top = target.getBoundingClientRect().top + window.scrollY - scrollMargin;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (line) {
          line.classList.remove('cart-item--just-added');
          void line.offsetWidth;
          line.classList.add('cart-item--just-added');
        }

        window.scrollTo({
          top: Math.max(0, top),
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      }
    });
  }
}